import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './TrainingConfigWindow.css';

export interface TrainingConfig {
  modelName: string;
  gpuType: string;
  description?: string;
  [key: string]: any; // Allow additional config fields
}

interface TrainingConfigWindowProps {
  onStartTraining: (config: TrainingConfig) => void;
  onCancel: () => void;
  defaultModelName?: string;
}

const TrainingConfigWindow: React.FC<TrainingConfigWindowProps> = ({
  onStartTraining,
  onCancel,
  defaultModelName = '',
}) => {
  const [modelName, setModelName] = useState(defaultModelName || '');
  const [gpuType, setGpuType] = useState('auto');
  const [description, setDescription] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!modelName.trim()) {
      alert('Please enter a model name');
      return;
    }

    const config: TrainingConfig = {
      modelName: modelName.trim(),
      gpuType,
      description: description.trim() || undefined,
    };

    onStartTraining(config);
  };

  return (
    <motion.div
      className="training-config-window"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <div className="training-config-content">
        <h2 className="training-config-title">Training Configuration</h2>
        <p className="training-config-subtitle">Configure your training parameters before starting</p>

        <form onSubmit={handleSubmit} className="training-config-form">
          <div className="training-config-field">
            <label htmlFor="modelName" className="training-config-label">
              Model Name <span className="required">*</span>
            </label>
            <input
              id="modelName"
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              className="training-config-input"
              placeholder="Enter a name for your model"
              required
            />
          </div>

          <div className="training-config-field">
            <label htmlFor="gpuType" className="training-config-label">
              GPU Type
            </label>
            <select
              id="gpuType"
              value={gpuType}
              onChange={(e) => setGpuType(e.target.value)}
              className="training-config-select"
            >
              <option value="auto">Auto (Use best available)</option>
              <option value="cuda">CUDA (NVIDIA GPU)</option>
              <option value="cpu">CPU</option>
              <option value="mps">MPS (Apple Silicon)</option>
            </select>
            <p className="training-config-hint">
              The system will use the best available device if your selection is not available.
            </p>
          </div>

          <div className="training-config-field">
            <label htmlFor="description" className="training-config-label">
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="training-config-textarea"
              placeholder="Add any notes or description about this training run..."
              rows={3}
            />
          </div>

          <div className="training-config-buttons">
            <button
              type="button"
              onClick={onCancel}
              className="training-config-btn training-config-btn-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="training-config-btn training-config-btn-start"
            >
              Start Training
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default TrainingConfigWindow;


